import { convertPointsToCurrency } from './points-exchange';

const DEFAULT_CURRENCY_CODE = 'USD';
const DEFAULT_LOCALE = 'en-US';

const getSafeCurrencyCode = (currencyCode?: string | null): string =>
  currencyCode?.trim().toUpperCase() || DEFAULT_CURRENCY_CODE;

/**
 * Formats a monetary amount with two decimal places.
 *
 * RewardBloom rewards can represent fractions of currencies whose customary
 * display has no minor unit, so we deliberately keep two decimals for every
 * supported display currency.
 */
export function formatCurrencyAmount(
  amount: number,
  currencyCode: string = DEFAULT_CURRENCY_CODE,
  locale: string = DEFAULT_LOCALE
): string {
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const safeCurrencyCode = getSafeCurrencyCode(currencyCode);

  try {
    return new Intl.NumberFormat(locale || DEFAULT_LOCALE, {
      style: 'currency',
      currency: safeCurrencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safeAmount);
  } catch {
    return `${safeCurrencyCode} ${safeAmount.toFixed(2)}`;
  }
}

/**
 * Converts the point-based accounting value into the user's currency and
 * formats it for display. Stored values remain point based.
 */
export function formatPointsAsCurrency(
  points: number,
  currencyCode: string = DEFAULT_CURRENCY_CODE,
  usdExchangeRate: number = 1,
  locale: string = DEFAULT_LOCALE
): string {
  return formatCurrencyAmount(convertPointsToCurrency(points, usdExchangeRate), currencyCode, locale);
}
